import { useState, useEffect, useCallback } from 'react';
import styles from './Configuracoes.module.css';
import { db } from '../../firebase';
import {
  doc,
  getDoc,
  setDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { usePermissao } from '../../hooks/usePermissao';

// ====================================================================
// CONFIGURACOES - Bloco 5.2
// 3 docs no Firestore, ID fixo "default":
//   - parametros/default            (prazoAceiteHoras, minCharsNegacao, etc)
//   - config_permissoes/default     (matriz operador/visualizador)
//   - config_notificacoes/default   (regras de notificacao)
//
// Movimentacao.jsx 4.3 ja le de parametros/default.
// Outras telas (alerta 120%, exigir OS, permitir saida zerada) seguem
// como TODO ate Bloco 5.3/6.
// ====================================================================

const ID_DOC = 'default';

// ----- Permissoes padrao (fallback se nunca foi salvo) -----
const PERMISSOES_PADRAO = [
  { grupo: 'Geral', itens: [
    { id: 'dashboard', label: 'Início', operador: true, visualizador: true },
    { id: 'estoque', label: 'Estoque', operador: true, visualizador: true },
  ]},
  { grupo: 'Movimentação', itens: [
    { id: 'mov-saida', label: 'Nova saída', operador: true, visualizador: false },
    { id: 'mov-edt', label: 'Entrada / Dev. / Transf.', operador: true, visualizador: false },
    { id: 'mov-aceite', label: 'Aceitar / Negar', operador: true, visualizador: false },
    { id: 'mov-hist', label: 'Histórico', operador: true, visualizador: true },
  ]},
  { grupo: 'Ordens de Serviço', itens: [
    { id: 'os-consulta', label: 'Consultar OS', operador: true, visualizador: true },
    { id: 'os-editar', label: 'Criar / Editar OS', operador: true, visualizador: false },
  ]},
  { grupo: 'Cadastros', itens: [
    { id: 'cad-prod', label: 'Produtos', operador: false, visualizador: false },
    { id: 'cad-estruturas', label: 'Unidades / Estoques / Clientes', operador: false, visualizador: false },
    { id: 'cad-users', label: 'Usuários', operador: false, visualizador: false },
  ]},
  { grupo: 'Sistema', itens: [
    { id: 'sis-rel', label: 'Relatórios', operador: true, visualizador: true },
    { id: 'sis-config', label: 'Configurações', operador: false, visualizador: false },
  ]},
];

// ----- Notificacoes padrao -----
const NOTIFICACOES_PADRAO = [
  { id: 'n-estouro', badge: 'OS', badgeClass: 'nbOs', nome: 'Estouro de OS', desc: 'Consumo real ultrapassou o previsto em uma OS', ativo: true, gestor: true, operador: true },
  { id: 'n-120', badge: 'OS', badgeClass: 'nbOs', nome: 'Consumo atingiu 120% do previsto', desc: 'Aviso antes do estouro: consumo chega a 120% do previsto', ativo: true, gestor: true, operador: true },
  { id: 'n-min', badge: 'Estoque', badgeClass: 'nbEstoque', nome: 'Produto abaixo do mínimo', desc: 'Qtd real ficou abaixo do mínimo configurado no estoque', ativo: true, gestor: true, operador: true },
  { id: 'n-aceite', badge: 'Movim.', badgeClass: 'nbMov', nome: 'Movimentação aguardando aceite', desc: 'Nova entrada/devolução/transferência aguarda confirmação', ativo: true, gestor: false, operador: true },
  { id: 'n-negada', badge: 'Movim.', badgeClass: 'nbMov', nome: 'Movimentação negada', desc: 'Responsável do destino recusou a movimentação', ativo: true, gestor: true, operador: true },
  { id: 'n-prazo', badge: 'Movim.', badgeClass: 'nbMov', nome: 'Prazo de aceite expirando', desc: 'Movimentação sem aceite no prazo — escalada ao Gestor', ativo: true, gestor: true, operador: true },
  { id: 'n-confirm', badge: 'Movim.', badgeClass: 'nbMov', nome: 'Aceite confirmado', desc: 'Movimentação aceita e estoque atualizado', ativo: false, gestor: true, operador: false },
];

// ----- Parametros padrao -----
// Nomes batem com o que o Movimentacao.jsx 4.3 ja le:
//   - prazoAceiteHoras  (numero de horas)
//   - minCharsNegacao   (numero de caracteres)
const PARAMETROS_PADRAO = {
  prazoAceiteHoras: 48,
  acaoVencimento: 'Escalar ao Gestor',
  alertaConsumoOS: 120,
  minCharsNegacao: 10,
  backup: 'Diário',
  permitirSaidaZerada: false,
  exigirOSnaSaida: true,
};

export default function Configuracoes() {
  const { pode } = usePermissao();

  const [aba, setAba] = useState('perfis');

  const [permissoes, setPermissoes] = useState(PERMISSOES_PADRAO);
  const [notificacoes, setNotificacoes] = useState(NOTIFICACOES_PADRAO);
  const [parametros, setParametros] = useState(PARAMETROS_PADRAO);

  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState('');
  const [erro, setErro] = useState('');
  const [mensagem, setMensagem] = useState('');

  // ----- Carregar dos 3 docs -----
  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro('');
    try {
      const pSnap = await getDoc(doc(db, 'parametros', ID_DOC));
      if (pSnap.exists()) {
        setParametros({ ...PARAMETROS_PADRAO, ...pSnap.data() });
      }
      const permSnap = await getDoc(doc(db, 'config_permissoes', ID_DOC));
      if (permSnap.exists() && Array.isArray(permSnap.data().matriz)) {
        setPermissoes(permSnap.data().matriz);
      }
      const nSnap = await getDoc(doc(db, 'config_notificacoes', ID_DOC));
      if (nSnap.exists() && Array.isArray(nSnap.data().regras)) {
        setNotificacoes(nSnap.data().regras);
      }
    } catch (e) {
      setErro(e.message || 'Erro ao carregar configurações.');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  function togglePermissao(grupoIdx, itemIdx, perfil) {
    setPermissoes((prev) =>
      prev.map((g, gi) =>
        gi !== grupoIdx
          ? g
          : {
              ...g,
              itens: g.itens.map((it, ii) =>
                ii !== itemIdx ? it : { ...it, [perfil]: !it[perfil] }
              ),
            }
      )
    );
  }

  function toggleNotif(id, campo) {
    setNotificacoes((prev) =>
      prev.map((n) => (n.id === id ? { ...n, [campo]: !n[campo] } : n))
    );
  }

  function setParam(campo, valor) {
    setParametros((prev) => ({ ...prev, [campo]: valor }));
  }

  function flashMensagem(msg) {
    setMensagem(msg);
    setTimeout(() => setMensagem(''), 3000);
  }

  async function salvarAba(secao) {
    if (salvando) return;
    setSalvando(secao);
    setErro('');
    try {
      if (secao === 'perfis') {
        await setDoc(
          doc(db, 'config_permissoes', ID_DOC),
          { matriz: permissoes, atualizadoEm: serverTimestamp() },
          { merge: true }
        );
        flashMensagem('Permissões salvas com sucesso.');
      } else if (secao === 'notif') {
        await setDoc(
          doc(db, 'config_notificacoes', ID_DOC),
          { regras: notificacoes, atualizadoEm: serverTimestamp() },
          { merge: true }
        );
        flashMensagem('Notificações salvas com sucesso.');
      } else if (secao === 'param') {
        await setDoc(
          doc(db, 'parametros', ID_DOC),
          { ...parametros, atualizadoEm: serverTimestamp() },
          { merge: true }
        );
        flashMensagem(
          'Parâmetros salvos com sucesso. As telas conectadas (Movimentação) já passam a usar.'
        );
      }
    } catch (e) {
      setErro(e.message || 'Erro ao salvar configurações.');
    } finally {
      setSalvando('');
    }
  }

  if (!pode('verConfiguracoes')) {
    return (
      <div
        style={{
          background: '#fdecea',
          color: '#b51013',
          border: '1px solid #f5c6cb',
          borderRadius: 8,
          padding: '24px',
          margin: '20px 0',
          fontSize: 15,
          textAlign: 'center',
        }}
      >
        <strong>Acesso restrito.</strong>
        <div style={{ marginTop: 8, fontSize: 13, color: '#7a1a1d' }}>
          Apenas usuários com perfil Gestor podem acessar Configurações.
        </div>
      </div>
    );
  }

  return (
    <>
      <div>
        <div className={styles.pageTitle}>Configurações</div>
        <div className={styles.pageSub}>Acesso restrito a perfil Gestor</div>
      </div>

      <div className={styles.avisoGestor}>
        <span>⚠️</span>
        <span>
          Estas configurações afetam <strong>todos os usuários</strong>. Apenas o
          perfil <strong>Gestor</strong> pode alterar.
        </span>
      </div>

      {erro && (
        <div
          style={{
            background: '#fdecea',
            color: '#b51013',
            border: '1px solid #f5c6cb',
            borderRadius: 8,
            padding: '10px 14px',
            margin: '0 0 14px',
            fontSize: 14,
          }}
        >
          {erro}
        </div>
      )}

      {mensagem && (
        <div
          style={{
            background: '#E1F5EE',
            color: '#0F6E56',
            border: '1px solid #5DCAA5',
            borderRadius: 8,
            padding: '10px 14px',
            margin: '0 0 14px',
            fontSize: 14,
          }}
        >
          ✓ {mensagem}
        </div>
      )}

      <div className={styles.tabs}>
        <div
          className={`${styles.tab} ${aba === 'perfis' ? styles.active : ''}`}
          onClick={() => setAba('perfis')}
        >
          Perfis e Permissões
        </div>
        <div
          className={`${styles.tab} ${aba === 'notif' ? styles.active : ''}`}
          onClick={() => setAba('notif')}
        >
          Notificações
        </div>
        <div
          className={`${styles.tab} ${aba === 'param' ? styles.active : ''}`}
          onClick={() => setAba('param')}
        >
          Parâmetros
        </div>
      </div>

      {carregando ? (
        <div style={{ padding: 30, textAlign: 'center', color: '#888' }}>
          Carregando configurações...
        </div>
      ) : (
        <>
          {/* ABA PERFIS */}
          {aba === 'perfis' && (
            <div className={styles.tabContent}>
              <div className={styles.panel}>
                <div className={styles.panelHead}>
                  <div className={styles.panelTitle}>Permissões por perfil</div>
                  <div className={styles.panelHint}>
                    Gestor tem acesso total (fixo)
                  </div>
                </div>
                <div className={styles.matrizHead}>
                  <div>Módulo / Ação</div>
                  <div className={styles.perfilColG}>Gestor</div>
                  <div className={styles.perfilColO}>Operador</div>
                  <div className={styles.perfilColV}>Visualizador</div>
                </div>
                {permissoes.map((grupo, gi) => (
                  <div key={grupo.grupo}>
                    <div className={`${styles.matrizRow} ${styles.grupo}`}>
                      {grupo.grupo}
                    </div>
                    {grupo.itens.map((item, ii) => (
                      <div key={item.id} className={styles.matrizRow}>
                        <div className={styles.matrizMod}>{item.label}</div>
                        <div className={styles.matrizCheck}>
                          <div className={`${styles.chk} ${styles.locked}`}>✓</div>
                        </div>
                        <div className={styles.matrizCheck}>
                          <div
                            className={`${styles.chk} ${
                              item.operador ? `${styles.on} ${styles.azul}` : ''
                            }`}
                            onClick={() => togglePermissao(gi, ii, 'operador')}
                          >
                            {item.operador ? '✓' : ''}
                          </div>
                        </div>
                        <div className={styles.matrizCheck}>
                          <div
                            className={`${styles.chk} ${
                              item.visualizador ? styles.on : ''
                            }`}
                            onClick={() => togglePermissao(gi, ii, 'visualizador')}
                          >
                            {item.visualizador ? '✓' : ''}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
              <div className={styles.notaInfo}>
                ℹ️ <strong>Nota técnica:</strong> os valores são salvos no
                Firestore. Fazer as outras telas respeitarem esta matriz
                (esconder botões/itens conforme perfil) ainda é trabalho pendente
                no Bloco 5.3+.
              </div>
              <div className={styles.salvarBar}>
                <button
                  className={styles.btnSalvar}
                  onClick={() => salvarAba('perfis')}
                  disabled={salvando === 'perfis'}
                >
                  {salvando === 'perfis' ? 'Salvando...' : 'Salvar alterações'}
                </button>
              </div>
            </div>
          )}

          {/* ABA NOTIFICACOES */}
          {aba === 'notif' && (
            <div className={styles.tabContent}>
              <div className={styles.panel}>
                <div className={styles.panelHead}>
                  <div className={styles.panelTitle}>Regras de notificação</div>
                  <button
                    className={styles.btnSalvar}
                    onClick={() => salvarAba('notif')}
                    disabled={salvando === 'notif'}
                  >
                    {salvando === 'notif' ? 'Salvando...' : 'Salvar alterações'}
                  </button>
                </div>
                <div className={styles.notifHead}>
                  <div>Evento</div>
                  <div>Ativo</div>
                  <div>Gestor</div>
                  <div>Operador</div>
                </div>
                {notificacoes.map((n) => (
                  <div key={n.id} className={styles.notifRow}>
                    <div className={styles.notifEvento}>
                      <span
                        className={`${styles.notifBadge} ${
                          styles[n.badgeClass] || ''
                        }`}
                      >
                        {n.badge}
                      </span>
                      <div>
                        <div className={styles.notifNome}>{n.nome}</div>
                        <div className={styles.notifDesc}>{n.desc}</div>
                      </div>
                    </div>
                    <div className={styles.notifCell}>
                      <div
                        className={`${styles.toggleSwitch} ${
                          n.ativo ? styles.on : ''
                        }`}
                        onClick={() => toggleNotif(n.id, 'ativo')}
                      />
                    </div>
                    <div className={styles.notifCell}>
                      <div
                        className={`${styles.chk} ${n.gestor ? styles.on : ''}`}
                        onClick={() => toggleNotif(n.id, 'gestor')}
                      >
                        {n.gestor ? '✓' : ''}
                      </div>
                    </div>
                    <div className={styles.notifCell}>
                      <div
                        className={`${styles.chk} ${n.operador ? styles.on : ''}`}
                        onClick={() => toggleNotif(n.id, 'operador')}
                      >
                        {n.operador ? '✓' : ''}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className={styles.notaInfo}>
                ℹ️ <strong>Nota técnica:</strong> o envio real (push/e-mail) é o
                Bloco 5.3 (Cloud Functions). Esta aba já grava quem deve receber.
              </div>
            </div>
          )}

          {/* ABA PARAMETROS */}
          {aba === 'param' && (
            <div className={styles.tabContent}>
              <div className={styles.panel}>
                <div className={styles.panelHead}>
                  <div className={styles.panelTitle}>Parâmetros do sistema</div>
                  <button
                    className={styles.btnSalvar}
                    onClick={() => salvarAba('param')}
                    disabled={salvando === 'param'}
                  >
                    {salvando === 'param' ? 'Salvando...' : 'Salvar alterações'}
                  </button>
                </div>

                <div className={styles.paramRow}>
                  <div>
                    <div className={styles.paramLabel}>
                      Prazo para aceite de movimentação
                    </div>
                    <div className={styles.paramHint}>
                      Horas antes de escalar ao Gestor sem aceite (já em uso pela Movimentação)
                    </div>
                  </div>
                  <div className={styles.paramControl}>
                    <div className={styles.numWrap}>
                      <input
                        className={styles.numInput}
                        type="number"
                        min="1"
                        value={parametros.prazoAceiteHoras}
                        onChange={(e) =>
                          setParam(
                            'prazoAceiteHoras',
                            parseInt(e.target.value, 10) || 0
                          )
                        }
                      />
                      <span className={styles.numSfx}>h</span>
                    </div>
                  </div>
                </div>

                <div className={styles.paramRow}>
                  <div>
                    <div className={styles.paramLabel}>
                      Ação quando o prazo vence
                    </div>
                    <div className={styles.paramHint}>
                      O que o sistema faz se não houver aceite no prazo (será aplicado no Bloco 5.3 - Functions)
                    </div>
                  </div>
                  <div className={styles.paramControl}>
                    <select
                      className={styles.sel}
                      style={{ width: 180 }}
                      value={parametros.acaoVencimento}
                      onChange={(e) => setParam('acaoVencimento', e.target.value)}
                    >
                      <option>Escalar ao Gestor</option>
                      <option>Apenas alertar</option>
                      <option>Cancelar automático</option>
                    </select>
                  </div>
                </div>

                <div className={styles.paramRow}>
                  <div>
                    <div className={styles.paramLabel}>
                      Alerta de consumo de OS (%)
                    </div>
                    <div className={styles.paramHint}>
                      Percentual do previsto que dispara alerta (será aplicado na tela OS no próximo passo)
                    </div>
                  </div>
                  <div className={styles.paramControl}>
                    <div className={styles.numWrap}>
                      <input
                        className={styles.numInput}
                        type="number"
                        min="100"
                        value={parametros.alertaConsumoOS}
                        onChange={(e) =>
                          setParam(
                            'alertaConsumoOS',
                            parseInt(e.target.value, 10) || 0
                          )
                        }
                      />
                      <span className={styles.numSfx}>%</span>
                    </div>
                  </div>
                </div>

                <div className={styles.paramRow}>
                  <div>
                    <div className={styles.paramLabel}>
                      Mínimo de caracteres no motivo de negação
                    </div>
                    <div className={styles.paramHint}>
                      Tamanho mínimo da justificativa (já em uso pela Movimentação)
                    </div>
                  </div>
                  <div className={styles.paramControl}>
                    <div className={styles.numWrap}>
                      <input
                        className={styles.numInput}
                        type="number"
                        min="0"
                        value={parametros.minCharsNegacao}
                        onChange={(e) =>
                          setParam(
                            'minCharsNegacao',
                            parseInt(e.target.value, 10) || 0
                          )
                        }
                      />
                      <span className={styles.numSfx}>car.</span>
                    </div>
                  </div>
                </div>

                <div className={styles.paramRow}>
                  <div>
                    <div className={styles.paramLabel}>Backup automático</div>
                    <div className={styles.paramHint}>
                      Frequência de backup dos dados (será aplicado no Bloco 6)
                    </div>
                  </div>
                  <div className={styles.paramControl}>
                    <select
                      className={styles.sel}
                      value={parametros.backup}
                      onChange={(e) => setParam('backup', e.target.value)}
                    >
                      <option>Diário</option>
                      <option>Semanal</option>
                      <option>Mensal</option>
                    </select>
                  </div>
                </div>

                <div className={styles.paramRow}>
                  <div>
                    <div className={styles.paramLabel}>
                      Permitir saída com estoque zerado
                    </div>
                    <div className={styles.paramHint}>
                      Se desativado, bloqueia saída quando qtd = 0 (será aplicado na Movimentação)
                    </div>
                  </div>
                  <div className={styles.paramControl}>
                    <div
                      className={`${styles.toggleSwitch} ${
                        parametros.permitirSaidaZerada ? styles.on : ''
                      }`}
                      onClick={() =>
                        setParam(
                          'permitirSaidaZerada',
                          !parametros.permitirSaidaZerada
                        )
                      }
                    />
                  </div>
                </div>

                <div className={styles.paramRow}>
                  <div>
                    <div className={styles.paramLabel}>Exigir OS em toda saída</div>
                    <div className={styles.paramHint}>
                      Toda saída obriga vínculo com OS cadastrada (será aplicado na Movimentação)
                    </div>
                  </div>
                  <div className={styles.paramControl}>
                    <div
                      className={`${styles.toggleSwitch} ${
                        parametros.exigirOSnaSaida ? styles.on : ''
                      }`}
                      onClick={() =>
                        setParam(
                          'exigirOSnaSaida',
                          !parametros.exigirOSnaSaida
                        )
                      }
                    />
                  </div>
                </div>
              </div>
              <div className={styles.notaInfo}>
                ℹ️ <strong>2 parâmetros já em uso:</strong> "Prazo de aceite" e
                "Mínimo de caracteres no motivo" — a tela Movimentação lê esses
                valores. Os demais conectam nas outras telas no próximo passo.
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
}